import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Tooltip, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionLinks,
  BannerSeverity,
  IActionLink,
  Icon,
  IconName,
  NotificationBanner,
  StatusTag,
} from "halifax";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  FiatPrice,
  HomesReservationStatus,
  MyTripsFilter,
  MyTripsModalTypes,
  TagColors,
} from "redmond";

import { ConfirmationSummary } from "../ConfirmationSummary";

import * as textConstants from "../ExperienceCard/constants";
import { MobileActionLinks } from "../MobileActionLinks";
import { SummaryCard } from "../SummaryCard";
import { MobileExperienceReservationDetailsConnectorProps } from "./container";

import "./styles.scss";
import { TravelersSummary } from "../TravelersSummary";
import { PaymentSummary } from "../PaymentSummary";
import { getCurrencyString } from "../../../../../../utils/helpers";
import { ExperienceCardContent } from "../ExperienceCard/components/ExperienceCardContent";
import { addExperienceType } from "../ExperienceCard/components";
import { ExperienceCancellationPolicyCard } from "../ExperienceCard";
import { useIsTravelApp } from "@capone/common";
import { config } from "../../../../../../api/config";

export interface IMobileExperienceReservationDetailsProps
  extends RouteComponentProps,
    MobileExperienceReservationDetailsConnectorProps {}

export const MobileExperienceReservationDetails = ({
  experienceReservation,
  history,
  setOpenModal,
  tripsFilter,
  setSelectedExperience,
}: IMobileExperienceReservationDetailsProps) => {
  if (!experienceReservation) {
    return null;
  }

  const { isTravelApp } = useIsTravelApp();

  const { title, confirmationCode, subtitle, supplierName, phone, email } =
    textConstants.getExperienceInfoDetails(experienceReservation);

  const onOpenModal = (type: MyTripsModalTypes) =>
    setOpenModal({
      type,
      selectedItinerary: addExperienceType(experienceReservation),
    });
  const { reservation, paymentBreakdown } = experienceReservation;
  const {
    experienceDetails,
    status,
    bookingDetails,
    bookingSession,
    ticketInfo,
  } = reservation;
  const { supplierInfo } = experienceDetails || {};

  const travelerNames = textConstants.getTravelerNames(bookingDetails);

  const isCancelled = status === HomesReservationStatus.Cancelled;
  const isPastTrips = tripsFilter === MyTripsFilter.PAST_TRIPS;

  const actions: IActionLink[] = [];
  const topActions: IActionLink[] = []; // render at the top of the page

  if (!isCancelled && !isPastTrips) {
    actions.push({
      content: textConstants.CANCEL_EXPERIENCE,
      onClick: () => onOpenModal(MyTripsModalTypes.CancelExperience),
    });
  }

  actions.push({
    content: textConstants.RESEND_CONFIRMATION,
    onClick: () => onOpenModal(MyTripsModalTypes.ResendConfirmation),
  });

  if (ticketInfo?.url) {
    actions.push({
      content: textConstants.SHOW_VOUCHER,
      onClick: () => {
        window.open(ticketInfo.url, "_blank");
      },
    });
  }

  return (
    <Box className="mobile-trip-details">
      <Box className="mobile-trip-details-header">
        {!isTravelApp && (
          <FontAwesomeIcon
            icon={faChevronLeft}
            onClick={() => {
              history.goBack();
              setSelectedExperience(null);
            }}
          />
        )}
        <Box className="experience-header-info">
          <Typography variant="subtitle2" className="name">
            {subtitle}
          </Typography>
          <Typography variant="body2">By {supplierInfo?.name}</Typography>
        </Box>
      </Box>
      {isCancelled && (
        <NotificationBanner
          className="status-banner"
          icon={<Icon name={IconName.ErrorAlert} />}
          label={textConstants.CANCELED_RES}
          severity={BannerSeverity.ERROR}
        />
      )}
      {topActions.length ? <ActionLinks actions={topActions} /> : null}
      <Box
        className="mobile-trip-card mobile-experience-summary-card"
      >
        {isCancelled && (
          <StatusTag
            className="title-status-tag"
            tagInfo={{
              label: textConstants.CANCELED,
              type: TagColors.RED,
            }}
          />
        )}
        <Box className="titles-and-confirmation-info">
          {subtitle && (
            <Typography variant="body1" className="trips-subtitle">
              {subtitle}
            </Typography>
          )}
          <Typography variant="body2" className="trips-title">
            {title}
          </Typography>
          <Box className="info-container">
            <Typography className="label" variant="caption">
              {textConstants.CONFIRMATION}
            </Typography>
            <Typography
              className={clsx("code", { warning: isCancelled })}
              variant="caption"
            >
              {confirmationCode}
            </Typography>
          </Box>
        </Box>
      </Box>
      <SummaryCard className="mobile-trip-card mobile-location-details">
        <ExperienceCardContent
          reservation={reservation}
          onOpenModal={(type: MyTripsModalTypes) => {
            setOpenModal({
              type,
              selectedItinerary: addExperienceType(experienceReservation),
            });
          }}
          isMobile
        />
        <Box className="details-container supplier-info">
          {supplierName && (
            <Typography variant="body1" className="name">
              {supplierName}
            </Typography>
          )}
          {phone && (
            <Typography variant="caption" className="phone">
              {phone}
            </Typography>
          )}
          {email && (
            <Typography variant="caption" className="email">
              {email}
            </Typography>
          )}
        </Box>
      </SummaryCard>

      {reservation.cancellationPolicy && (
        <SummaryCard className="mobile-trip-card">
          <ExperienceCancellationPolicyCard
            cancellationPolicy={reservation.cancellationPolicy}
            onOpenModal={onOpenModal}
          />
        </SummaryCard>
      )}
      <SummaryCard
        className="mobile-trip-card"
        action={
          travelerNames.length > 1 ? (
            <FontAwesomeIcon
              className="mobile-right-chevron"
              onClick={() => onOpenModal(MyTripsModalTypes.TravelersModal)}
              icon={faChevronRight}
            />
          ) : undefined
        }
      >
        <TravelersSummary
          label={
            <Box className="travellers-header-container">
              {travelerNames.length > 1 ? (
                textConstants.TRAVELERS(travelerNames)
              ) : (
                <>
                  {textConstants.PRIMARY_TRAVELER}
                  <Tooltip
                    title={textConstants.PRIMARY_TRAVELER_TOOLTIP}
                    tabIndex={0}
                    enterTouchDelay={0} // Show tooltip immediately on touch
                    leaveTouchDelay={5000} // Keep open longer
                    classes={{
                      tooltip: "consistent-tooltip-style",
                    }}
                  >
                    <Box
                      className="primary-traveler-tooltip"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      aria-label={textConstants.PRIMARY_TRAVELER_ARIA_LABEL}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <Icon name={IconName.InfoCircle} />
                    </Box>
                  </Tooltip>
                </>
              )}
            </Box>
          }
          travelers={travelerNames.join(", ")}
        />
      </SummaryCard>
      <SummaryCard
        className="mobile-payment-summary mobile-trip-card"
        action={
          <FontAwesomeIcon
            className="mobile-right-chevron"
            onClick={() => onOpenModal(MyTripsModalTypes.PaymentModal)}
            icon={faChevronRight}
          />
        }
      >
        <PaymentSummary
          tripTotalAmount={
            reservation.experienceDetails?.feesDueUponArrival
              ?.totalPriceWithMandatoryExtraCharges
              ? getCurrencyString({
                  currencyCode:
                    bookingSession?.totalSellAtPriceWithFees?.fiat.currencyCode,
                  currencySymbol:
                    bookingSession?.totalSellAtPriceWithFees?.fiat
                      .currencySymbol,
                  value:
                    reservation.experienceDetails?.feesDueUponArrival
                      ?.totalPriceWithMandatoryExtraCharges,
                } as FiatPrice)
              : getCurrencyString(
                  bookingSession?.totalSellAtPriceWithFees?.fiat as FiatPrice,
                )
          }
          cardLabel={
            textConstants.getLabelsFromPaymentBreakdown(paymentBreakdown)
              .cardLabel
          }
          cardAmount={textConstants.getValues(paymentBreakdown).cardValue}
          rewardsLabel={
            textConstants.getLabelsFromPaymentBreakdown(paymentBreakdown)
              .rewardLabel
          }
          rewardsAmount={textConstants.getValues(paymentBreakdown).rewardValue}
          showCardLabel={false}
          showRewardsLabel={false}
          showArrivalFeesLabel={Boolean(
            reservation.experienceDetails?.feesDueUponArrival
              ?.totalMandatoryExtraCharges,
          )}
          arrivalFeesAmount={
            reservation.experienceDetails?.feesDueUponArrival
              ?.totalMandatoryExtraCharges
              ? getCurrencyString({
                  currencyCode:
                    bookingSession?.totalSellAtPriceWithFees?.fiat.currencyCode,
                  currencySymbol:
                    bookingSession?.totalSellAtPriceWithFees?.fiat
                      .currencySymbol,
                  value:
                    reservation.experienceDetails.feesDueUponArrival
                      .totalMandatoryExtraCharges,
                } as FiatPrice)
              : undefined
          }
        />
      </SummaryCard>
      <SummaryCard className="mobile-trip-card confirmation-summary">
        <ConfirmationSummary confirmationCode={confirmationCode} />
      </SummaryCard>
      <MobileActionLinks actions={actions} />
    </Box>
  );
};
